import { ClimateApiConfig, ClimateApiRequestOptions, ApiRequestLog, ClimateApiErrorType } from './types';
import { ClimateApiError } from './ClimateApiError';

/**
 * Abstract base class for all Climate API services
 * Provides common functionality for HTTP requests, error handling, and logging
 */
export abstract class AbstractClimateService {
  protected config: ClimateApiConfig;
  
  constructor(config: ClimateApiConfig) {
    this.config = config;
  }

  /**
   * Abstract method to get the service-specific path
   */
  protected abstract getServicePath(): string;

  /**
   * Generate a correlation ID for request tracking
   */
  private generateCorrelationId(): string {
    return `climate-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Sleep function for retry delays
   */
  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get authentication headers for API requests
   */
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth-token');
    
    return {
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  /**
   * Log API request details
   */
  private logRequest(log: ApiRequestLog): void {
    if (!this.config.enableLogging) return;
    
    const logLevel = log.error ? 'error' : 'info';
    const duration = log.duration ? ` (${log.duration}ms)` : '';
    const status = log.statusCode ? ` [${log.statusCode}]` : '';
    
    console[logLevel](`Climate API ${log.method} ${log.url}${status}${duration}`, {
      correlationId: log.correlationId,
      error: log.error
    });
  }

  /**
   * Create AbortController with timeout
   */
  private createAbortController(timeout: number): AbortController {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), timeout);
    return controller;
  }

  /**
   * Execute HTTP request with retry logic and error handling
   */
  protected async request<T>(
    endpoint: string,
    options: ClimateApiRequestOptions = {}
  ): Promise<T> {
    const correlationId = this.generateCorrelationId();
    const url = `${this.config.baseUrl}${this.getServicePath()}${endpoint}`;
    const timeout = options.timeout || this.config.timeout;
    const maxRetries = options.retries !== undefined ? options.retries : this.config.retryAttempts;

    let lastError: ClimateApiError | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const startTime = Date.now();
      let response: Response | null = null;
      
      try {
        // Create AbortController for timeout
        const controller = this.createAbortController(timeout);
        
        // Prepare headers - avoid Content-Type for GET requests to prevent CORS preflight
        const headers: Record<string, string> = {
          'X-Correlation-ID': correlationId,
          ...this.getAuthHeaders(),
          ...options.headers as Record<string, string>
        };
        
        // Only add Content-Type for requests with body data (POST, PUT) to avoid CORS preflight issues
        // Skip Content-Type for FormData - browser will set it automatically with boundary
        if (options.method && ['POST', 'PUT', 'PATCH'].includes(options.method) && options.body && !(options.body instanceof FormData)) {
          headers['Content-Type'] = 'application/json';
        }

        // Make the request
        response = await fetch(url, {
          ...options,
          headers,
          signal: controller.signal
        });

        const duration = Date.now() - startTime;

        // Log successful request
        this.logRequest({
          method: options.method || 'GET',
          url,
          correlationId,
          timestamp: startTime,
          duration,
          statusCode: response.status
        });

        // Handle non-ok responses
        if (!response.ok) {
          const error = await ClimateApiError.fromResponse(response, correlationId);
          
          // Only retry if error is retryable and we have attempts left
          if (error.isRetryable() && attempt < maxRetries) {
            lastError = error;
            await this.sleep(this.config.retryDelay * Math.pow(2, attempt)); // Exponential backoff
            continue;
          }
          
          throw error;
        }

        // Parse and return response
        const data = await response.json();
        return data;

      } catch (error) {
        const duration = Date.now() - startTime;
        
        if (error instanceof ClimateApiError) {
          // Log API error
          this.logRequest({
            method: options.method || 'GET',
            url,
            correlationId,
            timestamp: startTime,
            duration,
            statusCode: error.statusCode,
            error: error.message
          });
          
          // Only retry if error is retryable and we have attempts left
          if (error.isRetryable() && attempt < maxRetries) {
            lastError = error;
            await this.sleep(this.config.retryDelay * Math.pow(2, attempt));
            continue;
          }
          
          throw error;
        }

        // Handle network/fetch errors
        const networkError = ClimateApiError.fromNetworkError(
          error instanceof Error ? error : new Error('Unknown error'),
          correlationId
        );

        // Log network error
        this.logRequest({
          method: options.method || 'GET',
          url,
          correlationId,
          timestamp: startTime,
          duration,
          error: networkError.message
        });

        // Only retry if error is retryable and we have attempts left
        if (networkError.isRetryable() && attempt < maxRetries) {
          lastError = networkError;
          await this.sleep(this.config.retryDelay * Math.pow(2, attempt));
          continue;
        }

        throw networkError;
      }
    }

    // If we get here, we've exhausted all retries
    throw lastError || new ClimateApiError(
      ClimateApiErrorType.SERVER_ERROR,
      'Request failed after all retry attempts',
      undefined,
      undefined,
      correlationId
    );
  }

  /**
   * Perform GET request
   */
  protected async get<T>(endpoint: string, options?: ClimateApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Perform POST request
   */
  protected async post<T>(
    endpoint: string, 
    data?: unknown, 
    options?: ClimateApiRequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Perform PUT request
   */
  protected async put<T>(
    endpoint: string, 
    data?: unknown, 
    options?: ClimateApiRequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Perform DELETE request
   */
  protected async delete<T>(endpoint: string, options?: ClimateApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Health check for the service endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: number }> {
    try {
      const response = await this.get<{ status?: string }>('/health');
      return {
        status: response.status || 'ok',
        timestamp: Date.now()
      };
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw new ClimateApiError(
        ClimateApiErrorType.SERVER_ERROR,
        'Health check failed',
        undefined,
        error
      );
    }
  }
} 